import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Package, Wrench, AlertTriangle, Minus, History, ArrowDown, ArrowUp, RotateCcw, Search, Camera, ImagePlus } from 'lucide-react';
import { ProductLink } from '../components/crm/EntityLinks';
import { formatCurrency } from '../utils/formatters';

// Stock movement reasons
const STOCK_REASONS = {
  IN: [
    { value: 'Achat fournisseur', label: 'Achat fournisseur' },
    { value: 'Retour client', label: 'Retour client' },
    { value: 'Don/Cadeau reçu', label: 'Don/Cadeau reçu' },
    { value: 'Autre entrée', label: 'Autre entrée' }
  ],
  OUT: [
    { value: 'Perte/Casse', label: 'Perte/Casse' },
    { value: 'Péremption', label: 'Péremption' },
    { value: 'Usage interne', label: 'Usage interne' },
    { value: 'Don/Cadeau', label: 'Don/Cadeau' },
    { value: 'Autre sortie', label: 'Autre sortie' }
  ]
};

const MOVEMENT_TYPE_LABELS = {
  IN: { label: 'Entrée', color: 'bg-green-100 text-green-800', icon: ArrowDown },
  OUT: { label: 'Sortie', color: 'bg-red-100 text-red-800', icon: ArrowUp },
  ADJUSTMENT: { label: 'Ajustement', color: 'bg-blue-100 text-blue-800', icon: RotateCcw },
  SALE: { label: 'Vente', color: 'bg-purple-100 text-purple-800', icon: ArrowUp },
  INVENTORY: { label: 'Inventaire', color: 'bg-orange-100 text-orange-800', icon: RotateCcw }
};

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showStockDialog, setShowStockDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showQuickAdjustDialog, setShowQuickAdjustDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [stockHistory, setStockHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('products');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all'); // Category filter
  const [quickAdjustType, setQuickAdjustType] = useState('OUT'); // Default to OUT for common use
  
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    type: 'PRODUCT',
    category_id: '',
    sell_price: '',
    buy_price: '',
    stock_quantity: 0,
    min_stock: 0,
    unit: 'unité',
    image_url: ''
  });

  const [productImageFile, setProductImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [stockForm, setStockForm] = useState({
    type: 'IN',
    quantity: '',
    unit_price: '',
    reason: ''
  });

  const [quickAdjustForm, setQuickAdjustForm] = useState({
    delta: 1,
    reason: ''
  });

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    type: 'PRODUCT',
    color: '#6B7280'
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products', { params: { limit: 100 } });
      setProducts(response.data.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      // Fetch only PRODUCT and SERVICE categories
      const [productCats, serviceCats] = await Promise.all([
        api.get('/categories?type=PRODUCT'),
        api.get('/categories?type=SERVICE')
      ]);
      setCategories([...productCats.data, ...serviceCats.data]);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      // Upload image first if selected
      let imageUrl = productForm.image_url;
      if (productImageFile) {
        setUploadingImage(true);
        const formData = new FormData();
        formData.append('file', productImageFile);
        formData.append('entity_type', 'Product');
        try {
          const uploadRes = await api.post('/settings/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          imageUrl = uploadRes.data.url;
        } catch (uploadError) {
          console.error('Upload error:', uploadError);
          toast.error('Erreur upload image');
          setUploadingImage(false);
          return;
        }
        setUploadingImage(false);
      }

      const data = { ...productForm, image_url: imageUrl };
      
      if (selectedProduct) {
        await api.put(`/products/${selectedProduct.id}`, data);
        toast.success('Produit mis à jour');
      } else {
        await api.post('/products', data);
        toast.success('Produit créé');
      }
      setShowProductDialog(false);
      resetProductForm();
      setProductImageFile(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleProductImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image trop volumineuse (max 5MB)');
        return;
      }
      setProductImageFile(file);
      // Preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProductForm({ ...productForm, image_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleStockSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/products/${selectedProduct.id}/stock`, stockForm);
      toast.success('Stock mis à jour');
      setShowStockDialog(false);
      setStockForm({ type: 'IN', quantity: '', unit_price: '', reason: '' });
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  // Quick stock adjustment (+/-)
  const handleQuickAdjust = async (e) => {
    e.preventDefault();
    try {
      const delta = quickAdjustType === 'OUT' ? -Math.abs(quickAdjustForm.delta) : Math.abs(quickAdjustForm.delta);
      await api.post(`/products/${selectedProduct.id}/quick-adjust`, {
        delta,
        reason: quickAdjustForm.reason
      });
      toast.success(`Stock ${quickAdjustType === 'OUT' ? 'diminué' : 'augmenté'} de ${Math.abs(quickAdjustForm.delta)}`);
      setShowQuickAdjustDialog(false);
      setQuickAdjustForm({ delta: 1, reason: '' });
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  // Inline quick adjustment (single click +/-)
  const handleInlineAdjust = async (product, delta) => {
    try {
      await api.post(`/products/${product.id}/quick-adjust`, {
        delta,
        reason: delta > 0 ? 'Ajout rapide' : 'Retrait rapide'
      });
      toast.success(`Stock ${delta > 0 ? '+' : ''}${delta}`);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Stock insuffisant');
    }
  };

  // Fetch stock history for a product
  const fetchStockHistory = async (product) => {
    setHistoryLoading(true);
    try {
      const response = await api.get(`/products/${product.id}`);
      setStockHistory(response.data.movements || []);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Open history dialog
  const openHistoryDialog = (product) => {
    setSelectedProduct(product);
    setShowHistoryDialog(true);
    fetchStockHistory(product);
  };

  // Open quick adjust dialog
  const openQuickAdjustDialog = (product, type = 'OUT') => {
    setSelectedProduct(product);
    setQuickAdjustType(type);
    setQuickAdjustForm({ delta: 1, reason: '' });
    setShowQuickAdjustDialog(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedCategory) {
        await api.put(`/categories/${selectedCategory.id}`, categoryForm);
        toast.success('Catégorie mise à jour');
      } else {
        await api.post('/categories', categoryForm);
        toast.success('Catégorie créée');
      }
      setShowCategoryDialog(false);
      resetCategoryForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Désactiver ce produit ?')) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success('Produit désactivé');
      fetchProducts();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Désactiver cette catégorie ?')) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success('Catégorie désactivée');
      fetchCategories();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: '',
      description: '',
      type: 'PRODUCT',
      category_id: '',
      sell_price: '',
      buy_price: '',
      stock_quantity: 0,
      min_stock: 0,
      unit: 'unité',
      image_url: ''
    });
    setSelectedProduct(null);
    setProductImageFile(null);
  };

  const resetCategoryForm = () => {
    setCategoryForm({ name: '', type: 'PRODUCT', color: '#6B7280' });
    setSelectedCategory(null);
  };

  const openEditProduct = (product) => {
    setSelectedProduct(product);
    setProductForm({
      name: product.name,
      description: product.description || '',
      type: product.type,
      category_id: product.category_id || '',
      sell_price: product.sell_price,
      buy_price: product.buy_price,
      stock_quantity: product.stock_quantity,
      min_stock: product.min_stock,
      unit: product.unit,
      image_url: product.image_url || ''
    });
    setProductImageFile(null);
    setShowProductDialog(true);
  };

  const openStockDialog = (product) => {
    setSelectedProduct(product);
    setShowStockDialog(true);
  };

  const openEditCategory = (category) => {
    setSelectedCategory(category);
    setCategoryForm({
      name: category.name,
      type: category.type,
      color: category.color
    });
    setShowCategoryDialog(true);
  };

  const productCategories = categories.filter(c => c.type === 'PRODUCT' || c.type === 'SERVICE');
  const lowStockProducts = products.filter(p => p.type === 'PRODUCT' && p.stock_quantity <= p.min_stock);
  const stockProducts = products.filter(p => p.type === 'PRODUCT');
  
  // Filter products by search term and category
  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = filterCategory === 'all' || 
      (filterCategory === 'none' ? !p.category_id : p.category_id?.toString() === filterCategory);
    return matchesSearch && matchesCategory;
  });
  
  const filteredStockProducts = stockProducts.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format date for history display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Produits & Services</h1>
          <p className="text-muted-foreground mt-1">Gestion du catalogue</p>
        </div>
      </div>

      {/* Low Stock Alert */}
      {lowStockProducts.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            <p className="text-orange-800">
              <span className="font-semibold">{lowStockProducts.length}</span> produit(s) en stock faible:
              {lowStockProducts.slice(0, 3).map(p => ` ${p.name}`).join(',')}
              {lowStockProducts.length > 3 && '...'}
            </p>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="products">Produits & Services</TabsTrigger>
          <TabsTrigger value="stock">
            Gestion Stock
            {lowStockProducts.length > 0 && (
              <Badge variant="destructive" className="ml-2 text-xs">{lowStockProducts.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="categories">Catégories</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <div className="flex flex-1 gap-2 max-w-xl">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Rechercher un produit..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-products"
                />
              </div>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[180px]" data-testid="filter-category">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  <SelectItem value="none">Sans catégorie</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => { resetProductForm(); setShowProductDialog(true); }} data-testid="add-product-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau produit/service
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Nom</th>
                        <th>Type</th>
                        <th>Catégorie</th>
                        <th>Prix vente</th>
                        <th>Prix achat</th>
                        <th>Stock</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product) => (
                        <tr key={product.id} className={!product.active ? 'opacity-50' : ''} data-testid={`product-row-${product.id}`}>
                          <td>
                            <div className="flex items-center gap-2">
                              {product.type === 'PRODUCT' ? (
                                <Package className="w-4 h-4 text-blue-500" />
                              ) : (
                                <Wrench className="w-4 h-4 text-purple-500" />
                              )}
                              <span className="font-medium">{product.name}</span>
                            </div>
                          </td>
                          <td>
                            <Badge variant={product.type === 'PRODUCT' ? 'default' : 'secondary'}>
                              {product.type === 'PRODUCT' ? 'Produit' : 'Service'}
                            </Badge>
                          </td>
                          <td>
                            {product.category && (
                              <Badge variant="outline" style={{ borderColor: product.category.color }}>
                                {product.category.name}
                              </Badge>
                            )}
                          </td>
                          <td className="font-semibold">{formatCurrency(product.sell_price)}</td>
                          <td className="text-muted-foreground">{formatCurrency(product.buy_price)}</td>
                          <td>
                            {product.type === 'PRODUCT' ? (
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => handleInlineAdjust(product, -1)}
                                  disabled={product.stock_quantity <= 0}
                                  data-testid={`stock-minus-${product.id}`}
                                >
                                  <Minus className="w-3 h-3" />
                                </Button>
                                <span className={`min-w-[3rem] text-center font-semibold ${product.stock_quantity <= product.min_stock ? 'text-red-600' : ''}`}>
                                  {product.stock_quantity}
                                </span>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => handleInlineAdjust(product, 1)}
                                  data-testid={`stock-plus-${product.id}`}
                                >
                                  <Plus className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 ml-1"
                                  onClick={() => openHistoryDialog(product)}
                                  title="Historique"
                                  data-testid={`stock-history-${product.id}`}
                                >
                                  <History className="w-3 h-3" />
                                </Button>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditProduct(product)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteProduct(product.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stock Management Tab */}
        <TabsContent value="stock" className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Rechercher un produit..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Low Stock Alert */}
          {lowStockProducts.length > 0 && (
            <Card className="border-orange-200 bg-orange-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-orange-800">
                  <AlertTriangle className="w-5 h-5" />
                  Stock faible ({lowStockProducts.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {lowStockProducts.map(p => (
                    <div key={p.id} className="flex items-center justify-between bg-white rounded-lg p-2 border border-orange-200">
                      <div>
                        <span className="font-medium">{p.name}</span>
                        <span className="text-red-600 ml-2 font-bold">{p.stock_quantity}/{p.min_stock}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openQuickAdjustDialog(p, 'IN')}
                        className="text-green-600 border-green-300 hover:bg-green-50"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Ajouter
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stock Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredStockProducts.map((product) => (
              <Card 
                key={product.id} 
                className={`${!product.active ? 'opacity-50' : ''} ${product.stock_quantity <= product.min_stock ? 'border-red-200 bg-red-50/50' : ''}`}
                data-testid={`stock-card-${product.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm line-clamp-1">{product.name}</h3>
                      {product.category && (
                        <Badge variant="outline" className="text-xs mt-1" style={{ borderColor: product.category.color }}>
                          {product.category.name}
                        </Badge>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 -mt-1 -mr-2"
                      onClick={() => openHistoryDialog(product)}
                    >
                      <History className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="flex items-center justify-center gap-3 py-3">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 rounded-full border-red-200 hover:bg-red-50 hover:border-red-400"
                      onClick={() => openQuickAdjustDialog(product, 'OUT')}
                      data-testid={`quick-out-${product.id}`}
                    >
                      <Minus className="w-5 h-5 text-red-600" />
                    </Button>
                    <div className="text-center min-w-[4rem]">
                      <span className={`text-3xl font-bold ${product.stock_quantity <= product.min_stock ? 'text-red-600' : ''}`}>
                        {product.stock_quantity}
                      </span>
                      <p className="text-xs text-muted-foreground">{product.unit}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 rounded-full border-green-200 hover:bg-green-50 hover:border-green-400"
                      onClick={() => openQuickAdjustDialog(product, 'IN')}
                      data-testid={`quick-in-${product.id}`}
                    >
                      <Plus className="w-5 h-5 text-green-600" />
                    </Button>
                  </div>
                  
                  <div className="flex justify-between text-xs text-muted-foreground border-t pt-2">
                    <span>Min: {product.min_stock}</span>
                    <span>Achat: {formatCurrency(product.buy_price)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {filteredStockProducts.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun produit avec stock trouvé</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { resetCategoryForm(); setShowCategoryDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle catégorie
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.filter(c => c.type !== 'EXPENSE').map((category) => (
              <Card key={category.id} className={!category.active ? 'opacity-50' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: category.color }}
                      />
                      <div>
                        <p className="font-medium">{category.name}</p>
                        <Badge variant="outline" className="text-xs">
                          {category.type}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditCategory(category)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteCategory(category.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedProduct ? 'Modifier le produit' : 'Nouveau produit/service'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleProductSubmit} className="space-y-4">
            <div>
              <Label>Nom *</Label>
              <Input
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type *</Label>
                <Select value={productForm.type} onValueChange={(v) => setProductForm({ ...productForm, type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PRODUCT">Produit (avec stock)</SelectItem>
                    <SelectItem value="SERVICE">Service (sans stock)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Catégorie</Label>
                <Select value={productForm.category_id} onValueChange={(v) => setProductForm({ ...productForm, category_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {productCategories.map((cat) => (
                      <SelectItem key={cat.id} value={String(cat.id)}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prix de vente *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={productForm.sell_price}
                  onChange={(e) => setProductForm({ ...productForm, sell_price: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Prix d'achat</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={productForm.buy_price}
                  onChange={(e) => setProductForm({ ...productForm, buy_price: e.target.value })}
                />
              </div>
              {productForm.type === 'PRODUCT' && (
                <>
                  <div>
                    <Label>Stock initial</Label>
                    <Input
                      type="number"
                      value={productForm.stock_quantity}
                      onChange={(e) => setProductForm({ ...productForm, stock_quantity: parseInt(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>Stock minimum</Label>
                    <Input
                      type="number"
                      value={productForm.min_stock}
                      onChange={(e) => setProductForm({ ...productForm, min_stock: parseInt(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>Unité</Label>
                    <Input
                      value={productForm.unit}
                      onChange={(e) => setProductForm({ ...productForm, unit: e.target.value })}
                      placeholder="unité, kg, L..."
                    />
                  </div>
                </>
              )}
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                rows={2}
              />
            </div>
            
            {/* Image upload */}
            <div>
              <Label>Photo du produit</Label>
              <div className="mt-2 flex items-center gap-4">
                {productForm.image_url ? (
                  <div className="relative w-24 h-24 rounded-lg overflow-hidden border">
                    <img src={productForm.image_url} alt="Preview" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full text-xs"
                      onClick={() => { setProductForm({ ...productForm, image_url: '' }); setProductImageFile(null); }}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center text-muted-foreground">
                    <ImagePlus className="w-8 h-8" />
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProductImageChange}
                    className="hidden"
                    id="product-image-input"
                  />
                  <label htmlFor="product-image-input">
                    <Button type="button" variant="outline" size="sm" asChild>
                      <span>
                        <Camera className="w-4 h-4 mr-2" />
                        {productForm.image_url ? 'Changer' : 'Ajouter photo'}
                      </span>
                    </Button>
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">Max 5MB, JPG/PNG</p>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowProductDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={uploadingImage}>
                {uploadingImage ? 'Upload...' : (selectedProduct ? 'Mettre à jour' : 'Créer')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stock Adjustment Dialog */}
      <Dialog open={showStockDialog} onOpenChange={setShowStockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajuster le stock - {selectedProduct?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleStockSubmit} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Stock actuel: <span className="font-semibold">{selectedProduct?.stock_quantity}</span>
            </p>
            <div>
              <Label>Type de mouvement *</Label>
              <Select value={stockForm.type} onValueChange={(v) => setStockForm({ ...stockForm, type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="IN">Entrée (achat, retour)</SelectItem>
                  <SelectItem value="OUT">Sortie (perte, casse)</SelectItem>
                  <SelectItem value="ADJUSTMENT">Ajustement (inventaire)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantité *</Label>
              <Input
                type="number"
                min="1"
                value={stockForm.quantity}
                onChange={(e) => setStockForm({ ...stockForm, quantity: parseInt(e.target.value) })}
                required
              />
            </div>
            <div>
              <Label>Prix unitaire</Label>
              <Input
                type="number"
                step="0.01"
                value={stockForm.unit_price}
                onChange={(e) => setStockForm({ ...stockForm, unit_price: e.target.value })}
              />
            </div>
            <div>
              <Label>Raison</Label>
              <Input
                value={stockForm.reason}
                onChange={(e) => setStockForm({ ...stockForm, reason: e.target.value })}
                placeholder="Achat fournisseur, inventaire..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowStockDialog(false)}>
                Annuler
              </Button>
              <Button type="submit">Appliquer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCategorySubmit} className="space-y-4">
            <div>
              <Label>Nom *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Type *</Label>
              <Select value={categoryForm.type} onValueChange={(v) => setCategoryForm({ ...categoryForm, type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PRODUCT">Produit</SelectItem>
                  <SelectItem value="SERVICE">Service</SelectItem>
                  <SelectItem value="CHARGE">Charge</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Couleur</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={categoryForm.color}
                  onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                  className="w-20 h-10 p-1"
                />
                <Input
                  value={categoryForm.color}
                  onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCategoryDialog(false)}>
                Annuler
              </Button>
              <Button type="submit">
                {selectedCategory ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Adjust Dialog */}
      <Dialog open={showQuickAdjustDialog} onOpenChange={setShowQuickAdjustDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {quickAdjustType === 'IN' ? (
                <>
                  <ArrowDown className="w-5 h-5 text-green-600" />
                  <span>Entrée stock - {selectedProduct?.name}</span>
                </>
              ) : (
                <>
                  <ArrowUp className="w-5 h-5 text-red-600" />
                  <span>Sortie stock - {selectedProduct?.name}</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleQuickAdjust} className="space-y-4">
            <div className="text-center py-2">
              <p className="text-sm text-muted-foreground">Stock actuel</p>
              <p className="text-3xl font-bold">{selectedProduct?.stock_quantity} <span className="text-sm font-normal text-muted-foreground">{selectedProduct?.unit}</span></p>
            </div>
            
            <div>
              <Label>Quantité *</Label>
              <Input
                type="number"
                min="1"
                value={quickAdjustForm.delta}
                onChange={(e) => setQuickAdjustForm({ ...quickAdjustForm, delta: parseInt(e.target.value) || 0 })}
                required
                className="text-center text-lg"
                data-testid="quick-adjust-quantity"
              />
            </div>
            
            <div>
              <Label>Raison</Label>
              <Select 
                value={quickAdjustForm.reason} 
                onValueChange={(v) => setQuickAdjustForm({ ...quickAdjustForm, reason: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une raison" />
                </SelectTrigger>
                <SelectContent>
                  {STOCK_REASONS[quickAdjustType].map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="text-center py-2 bg-gray-50 rounded-lg">
              <p className="text-sm text-muted-foreground">Nouveau stock</p>
              <p className="text-2xl font-bold">
                {quickAdjustType === 'IN' 
                  ? (selectedProduct?.stock_quantity || 0) + (quickAdjustForm.delta || 0)
                  : Math.max(0, (selectedProduct?.stock_quantity || 0) - (quickAdjustForm.delta || 0))
                }
              </p>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowQuickAdjustDialog(false)}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                className={quickAdjustType === 'IN' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                data-testid="submit-quick-adjust"
              >
                {quickAdjustType === 'IN' ? 'Ajouter' : 'Retirer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stock History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Historique stock - {selectedProduct?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="text-center py-3 bg-gray-50 rounded-lg mb-4">
            <p className="text-sm text-muted-foreground">Stock actuel</p>
            <p className="text-3xl font-bold">{selectedProduct?.stock_quantity} <span className="text-sm font-normal">{selectedProduct?.unit}</span></p>
          </div>
          
          <div className="overflow-y-auto max-h-[50vh]">
            {historyLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : stockHistory.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Aucun mouvement enregistré</p>
              </div>
            ) : (
              <div className="space-y-2">
                {stockHistory.map((movement) => {
                  const typeInfo = MOVEMENT_TYPE_LABELS[movement.type] || MOVEMENT_TYPE_LABELS.ADJUSTMENT;
                  const Icon = typeInfo.icon;
                  return (
                    <div key={movement.id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50" data-testid={`movement-${movement.id}`}>
                      <div className={`p-2 rounded-full ${typeInfo.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                          <span className={`font-semibold ${movement.type === 'IN' ? 'text-green-600' : movement.type === 'OUT' || movement.type === 'SALE' ? 'text-red-600' : 'text-blue-600'}`}>
                            {movement.type === 'IN' ? '+' : movement.type === 'OUT' || movement.type === 'SALE' ? '-' : ''}
                            {movement.quantity}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">{movement.reason || '-'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">{formatDate(movement.createdAt)}</p>
                        <p className="text-sm">
                          <span className="text-muted-foreground">{movement.previous_stock}</span>
                          <span className="mx-1">→</span>
                          <span className="font-semibold">{movement.new_stock}</span>
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>
              Fermer
            </Button>
            <Button onClick={() => { setShowHistoryDialog(false); openStockDialog(selectedProduct); }}>
              Ajustement complet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
